<template>
  <b-modal
    ref="modal"
    :id="modalId"
    :title="title"
    ok-title="Use image"
    ok-variant="primary"
    :ok-disabled="!hasSelection || !!error"
    size="lg"
    body-class="p-0"
    centered
    @hidden="onModalHidden"
  >
    <div class="px-5 py-4 pb-5 modal-body-content">
      <image-file-input
        class="m-0"
        :id="`${modalId}-file-input`"
        :file="!multiple ? pendingFile || file : null"
        :multiple="multiple"
        :upload-text="
          multiple
            ? 'Drag and Drop or <u>Click to upload</u> (multiple allowed)'
            : 'Drag and Drop or <u>Click to upload</u>'
        "
        :supported-text="supportedText"
        @change="onInputChange"
      />
      <div v-if="multiple && pendingFiles.length" class="mt-2">{{ pendingFiles.length }} image(s) selected</div>
      <div class="mt-3 d-flex flex-column align-items-center" v-if="!multiple && (pendingPreviewUrl || previewUrl)">
        <div class="label mb-2">Preview</div>
        <div :class="previewClass">
          <img :src="pendingPreviewUrl || previewUrl" alt="Selected image preview" class="modal-preview-image" />
        </div>
      </div>
    </div>

    <template #modal-footer>
      <small v-if="error" class="text-danger mr-auto">{{ error }}</small>
      <b-button variant="primary" @click="confirm">{{ multiple ? 'Use images' : 'Use image' }}</b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import ImageFileInput from '@/pages/recognition/components/ImageFileInput.vue';

const props = defineProps({
  modalId: { type: String, required: true },
  title: { type: String, default: 'Upload image' },
  acceptedFileTypes: { type: Array, default: () => ['image/jpeg', 'image/png'] },
  maxSizeBytes: { type: Number, default: 10 * 1024 * 1024 },
  file: { type: [Object, File], default: null },
  previewUrl: { type: String, default: '' },
  previewType: { type: String, default: 'banner' },
  supportedText: { type: String, default: undefined },
  multiple: { type: Boolean, default: false },
});

const emit = defineEmits(['update:file', 'update:previewUrl', 'update:files']);

const pendingFile = ref(null);
const pendingPreviewUrl = ref('');
const pendingFiles = ref([]);
const error = ref('');

const hasSelection = computed(() =>
  props.multiple ? pendingFiles.value.length > 0 : Boolean(pendingFile.value || props.file),
);
const modal = ref(null);
const previewClass = computed(() => (props.previewType === 'badge' ? 'badge-preview' : 'banner-preview'));

const revokeObjectUrl = url => {
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
};

const resetState = () => {
  error.value = '';
  if (pendingPreviewUrl.value) {
    revokeObjectUrl(pendingPreviewUrl.value);
  }
  pendingFile.value = null;
  pendingPreviewUrl.value = '';
  pendingFiles.value = [];
};

const onModalHidden = () => {
  resetState();
};

const validateFile = file => {
  if (file.size > props.maxSizeBytes) {
    error.value = 'Image must be 10MB or less.';
    return false;
  }
  if (file.type && !props.acceptedFileTypes.includes(file.type)) {
    error.value = 'Only JPG or PNG images are allowed.';
    return false;
  }
  return true;
};

const onInputChange = event => {
  const files = [...(event?.target?.files || event?.dataTransfer?.files || [])];
  if (!files.length) {
    return;
  }
  if (props.multiple) {
    const valid = [];
    let rejectionReason = '';
    for (const file of files) {
      if (validateFile(file)) {
        valid.push(file);
      } else if (!rejectionReason) {
        rejectionReason = error.value;
      }
    }
    error.value =
      valid.length < files.length ? `${files.length - valid.length} file(s) skipped: ${rejectionReason}` : '';
    if (valid.length) {
      pendingFiles.value = [...pendingFiles.value, ...valid];
    }
    return;
  }
  const file = files[0];
  if (!validateFile(file)) {
    return;
  }
  if (pendingPreviewUrl.value) {
    revokeObjectUrl(pendingPreviewUrl.value);
  }
  pendingFile.value = file;
  pendingPreviewUrl.value = URL.createObjectURL(file);
  error.value = '';
};

const confirm = () => {
  if (props.multiple) {
    if (!pendingFiles.value.length) {
      return;
    }
    emit('update:files', pendingFiles.value);
    pendingFiles.value = [];
    modal.value?.hide();
    return;
  }
  if (!pendingFile.value) {
    return;
  }
  if (props.previewUrl) {
    revokeObjectUrl(props.previewUrl);
  }
  emit('update:file', pendingFile.value);
  emit('update:previewUrl', pendingPreviewUrl.value);
  pendingFile.value = null;
  pendingPreviewUrl.value = '';

  modal.value?.hide();
};
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

:deep(.image-file-input__label) {
  margin: 0;
}

.label {
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.5rem;
  color: var(--palette-color-default);
}

.banner-preview {
  width: 16rem;
  background: var(--palette-color-core-1-lighten-90);
  border: 1px dashed var(--palette-color-default-lighten-50);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.banner-preview .modal-preview-image {
  height: auto;
  object-fit: unset;
}

.badge-preview {
  height: 9rem;
  width: 9rem;
  background: var(--palette-color-core-1-lighten-90);
  border: 1px dashed var(--palette-color-default-lighten-50);
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
</style>
